import { useEffect, useRef } from 'react';

import { supabase } from '../../../lib/supabase';

/**
 * Hook to keep the authentication session alive during multiplayer games
 * Prevents session expiry that causes 401/403 errors during gameplay
 */
export function useSessionKeepalive(isActive: boolean = true) {
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const lastRefreshRef = useRef<number>(0);

  useEffect(() => {
    if (!isActive) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    const refreshSession = async () => {
      try {
        const now = Date.now();
        // Only refresh if more than 5 minutes have passed since last refresh
        if (now - lastRefreshRef.current < 5 * 60 * 1000) {
          return;
        }

        console.log('[SessionKeepalive] Checking session status...');
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error('[SessionKeepalive] Error getting session:', error);
          return;
        }

        if (!session) {
          console.warn('[SessionKeepalive] No active session found');
          return;
        }

        // Check if token is about to expire (within next 10 minutes)
        const expiresAt = session.expires_at;
        if (expiresAt) {
          const expiryTime = expiresAt * 1000; // Convert to milliseconds
          const timeUntilExpiry = expiryTime - now;
          const tenMinutes = 10 * 60 * 1000;

          if (timeUntilExpiry < tenMinutes) {
            console.log('[SessionKeepalive] Token expiring soon, refreshing...');
            const { error: refreshError } = await supabase.auth.refreshSession();
            if (refreshError) {
              console.error('[SessionKeepalive] Failed to refresh session:', refreshError);
            } else {
              console.log('[SessionKeepalive] Session refreshed successfully');
              lastRefreshRef.current = now;
            }
          } else {
            console.log(`[SessionKeepalive] Session valid, expires in ${Math.round(timeUntilExpiry / 60000)} minutes`);
          }
        }
      } catch (error) {
        console.error('[SessionKeepalive] Unexpected error:', error);
      }
    };

    // Initial check
    refreshSession();

    // Set up interval to check every 5 minutes
    intervalRef.current = setInterval(refreshSession, 5 * 60 * 1000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isActive]);

  return {
    // Expose manual refresh function
    refreshSession: async () => {
      try {
        const { error } = await supabase.auth.refreshSession();
        if (error) {
          console.error('[SessionKeepalive] Manual refresh failed:', error);
          return false;
        }
        console.log('[SessionKeepalive] Manual refresh successful');
        lastRefreshRef.current = Date.now();
        return true;
      } catch (error) {
        console.error('[SessionKeepalive] Manual refresh error:', error);
        return false;
      }
    }
  };
}